<template>
  <TabPanel
    :value="value"
    :as="as"
    :as-child="asChild"
    :unstyled="true"
    :pt="passThrough"
    class="meridia-tab-panel"
    v-bind="$attrs"
  >
    <!--
      KeepAlive mode:
      PrimeVue TabPanel is in NON-LAZY mode (v-show), so it stays in the DOM.
      We manage visibility ourselves with v-if on MeridiaTabPanelContent.
      KeepAlive caches the wrapper when v-if becomes false, firing onDeactivated.
      When v-if becomes true again, it restores from cache, firing onActivated.
    -->
    <KeepAlive v-if="shouldKeepAlive">
      <MeridiaTabPanelContent
        v-if="isActive"
        :key="value"
        :class="animationClass"
      >
        <slot />
      </MeridiaTabPanelContent>
    </KeepAlive>

    <!-- Non-KeepAlive mode: PrimeVue handles visibility via v-show. -->
    <MeridiaTabPanelContent v-else :class="animationClass">
      <slot />
    </MeridiaTabPanelContent>
  </TabPanel>
</template>

<script setup lang="ts">
import { computed, inject, onMounted, onUnmounted } from 'vue';
import TabPanel from 'primevue/tabpanel';
import { usePassThrough } from 'primevue/passthrough';
import type { PassThrough } from '@primevue/core';
import type { TabPanelPassThroughOptions } from 'primevue/tabpanel';
import type { MeridiaTabPanelProps } from 'src/design_system/components/tab/types';
import {
  MERIDIA_TABS_REGISTER_TAB,
  MERIDIA_TABS_UNREGISTER_TAB,
} from 'src/design_system/components/tab/types';
import MeridiaTabPanelContent from 'src/design_system/components/tab/MeridiaTabPanelContent.vue';
import { useTabPanelAnimation } from 'src/design_system/components/tab/useTabPanelAnimation';

const props = withDefaults(defineProps<MeridiaTabPanelProps>(), {
  keepAlive: null,
});

defineSlots<{ default: () => any }>();

const { isActive, shouldKeepAlive, animationClass } =
  useTabPanelAnimation(props);

// Register this panel's value for tab-order tracking so directional (slide) transitions
// are driven by the panels themselves — no MeridiaTabList required. Panels are always
// present, unlike headers. MeridiaTab headers register the same values and registerTab
// de-dupes, so both can coexist without changing the resulting order.
const registerTab = inject(MERIDIA_TABS_REGISTER_TAB, undefined);
const unregisterTab = inject(MERIDIA_TABS_UNREGISTER_TAB, undefined);

onMounted(() => {
  if (registerTab && props.value != null) registerTab(props.value);
});

onUnmounted(() => {
  if (unregisterTab && props.value != null) unregisterTab(props.value);
});

const defaultPt: TabPanelPassThroughOptions = {
  root: {
    class:
      'meridia-tab-panel-root flex flex-col flex-1 overflow-hidden min-h-0 w-full min-w-0 p-0',
  },
};

const passThrough = computed(
  () =>
    usePassThrough(props.pt, defaultPt, {
      mergeSections: false,
      mergeProps: true,
    }) as PassThrough<TabPanelPassThroughOptions>
);
</script>
